import { useMemo } from 'react'
import { unique } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { excludeNullOrUndefined } from 'checkout/utils'
import { useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import { isSearchStreamUnavailable } from 'lib/maps/mapboxSearchMap'
import { getOfferId, isLoadedOffer, resolveSearchMapDisplayOffer } from './utils'

/**
 * Resolves offers for map pins.
 *
 * Control (`enableStreamMetaPins` false): current page + selected only.
 * Treatment: selected → mapOfferIds → current page → remaining list; optional markerCap.
 */
export default function useSearchMapResults(
  selectedOfferIds: Array<string>,
  overrideOfferList?: App.OfferList,
  pageSize: number = 20,
  offerList?: App.OfferList,
  filters: App.OfferListFilters = {},
  enableStreamMetaPins: boolean = false,
  markerCap?: number,
) {
  const offerMetaDataKey = getOfferListKey(filters)
  const streamMetaByOfferId = useAppSelector(
    state => state.offer.searchResultMetadata.offerMetaData[offerMetaDataKey],
  )

  const offerIds = useMemo(() => {
    if (!enableStreamMetaPins) {
      if (!offerList?.fetching && overrideOfferList) {
        return unique([...overrideOfferList.offerIds, ...selectedOfferIds])
      }

      return []
    }

    const orderedIds = unique([
      ...selectedOfferIds,
      ...(offerList?.mapOfferIds ?? []),
      ...(overrideOfferList?.offerIds ?? []),
      ...(offerList?.offerIds ?? []),
    ])

    return markerCap ? orderedIds.slice(0, markerCap) : orderedIds
  }, [
    enableStreamMetaPins,
    markerCap,
    offerList?.fetching,
    offerList?.mapOfferIds,
    offerList?.offerIds,
    overrideOfferList,
    selectedOfferIds,
  ])

  const [loadedOffers, offersFetching] = useOffers<App.HotelOffer | App.BedbankOffer>(offerIds)

  const offers = useMemo(() => {
    const loadedOfferMap = new Map(loadedOffers.map(offer => [offer.id, offer]))
    const resolved = offerIds
      .map(offerId => resolveSearchMapDisplayOffer(
        loadedOfferMap.get(offerId),
        streamMetaByOfferId?.[offerId],
      ))
      .filter(excludeNullOrUndefined)

    // Control keeps public offers only; treatment may use stream-meta pins.
    return enableStreamMetaPins ? resolved : resolved.filter(isLoadedOffer)
  }, [enableStreamMetaPins, loadedOffers, offerIds, streamMetaByOfferId])

  const totalNumOffers = overrideOfferList?.offerCount || 0
  const paginatedOfferCount = overrideOfferList?.offerIds?.length ?? 0

  const fetchingList = (offerList?.offerIds?.length || 0) < totalNumOffers
  // Page window still filling — drives the "Loading offers" banner to match sidepanel tiles.
  const loadingOffersBanner = paginatedOfferCount < pageSize &&
    fetchingList &&
    !overrideOfferList?.streamEnded
  // Pin/camera paths: treatment also requires active list fetching so these do not stick
  // after the first flush while the stream continues.
  const fetchingPage = loadingOffersBanner &&
    (!enableStreamMetaPins || !!offerList?.fetching)
  const error = overrideOfferList?.error && !overrideOfferList?.streamEnded
  // Unavailable stream IDs are dropped from `offers`; treat them as resolved for readiness.
  const resolvedOfferIds = new Set(offers.map(getOfferId))
  const offersReadyForPins = enableStreamMetaPins && offerIds.length > 0 && offerIds.every((offerId) => (
    isSearchStreamUnavailable(streamMetaByOfferId?.[offerId]) || resolvedOfferIds.has(offerId)
  ))
  const streamMetaOffersLoaded = offerIds.length === 0 ||
    (offersReadyForPins && !offersFetching && loadedOffers.length === offerIds.length)
  const allOffersLoaded = enableStreamMetaPins
    ? !fetchingList && streamMetaOffersLoaded
    : !fetchingList && !offersFetching && offers.length === totalNumOffers
  const noOffersAvailable = !fetchingList && totalNumOffers === 0
  const key = overrideOfferList?.key
  const offersInteractable = enableStreamMetaPins ? offersReadyForPins : allOffersLoaded

  return useMemo(() => ({
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    offersReadyForPins,
    offersInteractable,
    noOffersAvailable,
    key,
    fetchingPage,
    loadingOffersBanner,
  }), [
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    offersReadyForPins,
    offersInteractable,
    noOffersAvailable,
    key,
    fetchingPage,
    loadingOffersBanner,
  ])
}
